/**
 * NotificationBell — bell icon with unread count badge + dropdown list.
 * Used in AdminBar and DashboardPage header.
 *
 * Polling: every 30 s with AbortController — cancels gracefully on unmount.
 * Network errors are silently swallowed (transient / deploy windows).
 *
 * After any mutation (delete / clear / mark-read) we re-fetch from the server
 * to confirm persistence, preventing ghost badges caused by stale local state.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Check, CheckCheck, Trash2, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  NOTIFICATION_TYPE_CONFIG,
  type NotificationItem,
} from "../../utils/notificationsApi";
import { getSessionToken } from "../../utils/userApi";

const btnReset = "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

function formatTime(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60_000) return "\u05E2\u05DB\u05E9\u05D9\u05D5";
  if (diff < 3600_000) return `\u05DC\u05E4\u05E0\u05D9 ${Math.floor(diff / 60_000)} \u05D3\u05E7\u05F3`;
  if (diff < 86400_000) return `\u05DC\u05E4\u05E0\u05D9 ${Math.floor(diff / 3600_000)} \u05E9\u05E2\u05D5\u05EA`;
  const d = new Date(ts);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

/** Returns true for errors that are benign (network / abort / deploy window) */
function isTransientError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (err instanceof TypeError && /failed to fetch|network/i.test(err.message)) return true;
  if (err instanceof Error && /abort|failed to fetch|network|401|expired session/i.test(err.message)) return true;
  return false;
}

interface Props {
  /** Extra className for the bell wrapper */
  className?: string;
  /** Dark mode (used in AdminBar) */
  dark?: boolean;
}

export function NotificationBell({ className = "", dark = false }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  /** AbortController for the in-flight fetch — cancelled on unmount */
  const abortRef = useRef<AbortController | null>(null);
  /** Track consecutive failures for back-off */
  const failCountRef = useRef(0);
  /** Flag: component is mounted */
  const mountedRef = useRef(true);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Fetch with abort support
  const fetchNotifications = useCallback(async () => {
    if (!user?.username) return;
    if (!getSessionToken()) return; // No valid session — skip silently

    // Cancel any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const list = await getNotifications(user.username);
      // If this controller was aborted while awaiting, bail out
      if (controller.signal.aborted) return;
      if (!mountedRef.current) return;
      setNotifications(list);
      failCountRef.current = 0; // Reset on success
    } catch (err) {
      if (controller.signal.aborted) return;
      // Only log non-transient errors
      if (!isTransientError(err)) {
        console.warn("[NotificationBell] Unexpected fetch error:", err);
      }
      failCountRef.current += 1;
    }
  }, [user?.username]);

  // Poll: immediate + every 30 s (backs off to 60 s after 3 consecutive failures)
  useEffect(() => {
    mountedRef.current = true;
    fetchNotifications();

    const id = setInterval(() => {
      // Back off after repeated failures
      if (failCountRef.current >= 3) {
        // Still try, but less frequently (skip every other tick)
        if (failCountRef.current % 2 === 0) return;
      }
      fetchNotifications();
    }, 30_000);

    return () => {
      mountedRef.current = false;
      clearInterval(id);
      // Abort any in-flight fetch on unmount
      abortRef.current?.abort();
    };
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleMarkAllRead = async () => {
    if (!user?.username) return;
    setLoading(true);
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    await markAllAsRead(user.username);
    // Re-fetch to confirm server state
    await fetchNotifications();
    if (mountedRef.current) setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!user?.username) return;
    // Optimistic update
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    await deleteNotification(user.username, id);
    // Re-fetch to confirm server state
    await fetchNotifications();
  };

  const handleClear = async () => {
    if (!user?.username) return;
    setLoading(true);
    // Optimistic update
    setNotifications([]);
    await clearAllNotifications(user.username);
    // Re-fetch to confirm server state
    await fetchNotifications();
    if (mountedRef.current) setLoading(false);
  };

  const handleClick = async (n: NotificationItem) => {
    if (!n.read && user?.username) {
      await markAsRead(user.username, [n.id]);
      setNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.link) {
      // If the notification has a commentId, store it so CommentSystem can highlight it
      if ((n as any).commentId) {
        try { sessionStorage.setItem("zap_highlight_comment", (n as any).commentId); } catch {}
      }
      navigate(n.link);
      setOpen(false);
    }
  };

  if (!user) return null;

  const textColor = dark ? "text-white/80 hover:text-white" : "text-gray-600 hover:text-gray-900";
  const panelBg = "bg-white";

  return (
    <div className={`relative ${className}`} style={{ direction: "rtl" }}>
      {/* Bell button */}
      <button
        ref={bellRef}
        type="button"
        data-tour-step="notification-bell"
        className={`${btnReset} relative p-1.5 rounded-lg transition-transform ${textColor}`}
        onClick={() => setOpen(!open)}
        title={"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA"}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -left-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
            style={{ lineHeight: 1 }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={panelRef}
          className={`absolute top-full mt-2 left-0 w-[360px] max-h-[480px] rounded-xl shadow-2xl border border-gray-200 ${panelBg} z-[9999] flex flex-col overflow-hidden`}
          style={{ direction: "rtl" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80">
            <h3 className="text-sm font-bold text-gray-800">{"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA"}</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className={`${btnReset} text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50`}
                  onClick={handleMarkAllRead}
                  disabled={loading}
                >
                  <CheckCheck className="w-3.5 h-3.5 inline ml-1" />
                  {"\u05E1\u05DE\u05DF \u05D4\u05DB\u05DC"}
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  type="button"
                  className={`${btnReset} text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50`}
                  onClick={handleClear}
                  disabled={loading}
                >
                  <Trash2 className="w-3.5 h-3.5 inline ml-1" />
                  {"\u05E0\u05E7\u05D4"}
                </button>
              )}
              <button
                type="button"
                className={`${btnReset} p-1 text-gray-400 hover:text-gray-600 rounded`}
                onClick={() => setOpen(false)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Bell className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm">{"\u05D0\u05D9\u05DF \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA"}</p>
              </div>
            ) : (
              notifications.map((n) => {
                const cfg = NOTIFICATION_TYPE_CONFIG[n.type] || NOTIFICATION_TYPE_CONFIG.system;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50/80 ${
                      !n.read ? "bg-blue-50/40" : ""
                    }`}
                    onClick={() => handleClick(n)}
                  >
                    {/* Icon */}
                    <span
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${cfg.color}`}
                    >
                      {cfg.icon}
                    </span>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {n.title}
                        </span>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-gray-400">{formatTime(n.createdAt)}</span>
                        {n.templateName && (
                          <span className="text-[10px] text-gray-400 truncate max-w-[120px]">
                            {n.templateName}
                          </span>
                        )}
                        {n.link && (
                          <ExternalLink className="w-3 h-3 text-gray-300" />
                        )}
                      </div>
                    </div>

                    {/* Delete */}
                    <button
                      type="button"
                      className={`${btnReset} p-1 text-gray-300 hover:text-red-500 flex-shrink-0`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(n.id);
                      }}
                      style={{ opacity: 1 }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}